import { Box, Drawer, IconButton, Toolbar, Typography, useMediaQuery, useTheme,alpha, Tooltip } from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, { FC, useLayoutEffect } from 'react';
import { useTranslations } from 'use-intl';

import useNavigationLink from '../../../../hooks/useNavigationLink';
import CasaLogo from '../../../../shared/logo/CasaLogo';
import SidebarList from '../SidebarList/SidebarList';
import styles from './Sidebar.module.scss';
import useAuth from '../../../../hooks/useAuth';
import { capitalize } from 'lodash';
import { useTenantSelection } from '../../../../features/TenantSelection/contexts/TenantSelection';
import config from '../../../../config';
  
interface SidebarProps {
  isSidebarActive: boolean;
  onCloseSidebar: () => void;
}

const Sidebar: FC<SidebarProps> = ({ isSidebarActive, onCloseSidebar }) => {
  const { navigationLinks, onActive, fullScreenMode } = useNavigationLink();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('xl'));
  const { currentUser } = useAuth();
  const t = useTranslations('sidebar');
  const { open } = useTenantSelection();
  useLayoutEffect(() => {
    !isMobileView && onCloseSidebar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileView]);

  const sidebarContent = (
    <Box id={styles.sidebar} component='aside'>
        {currentUser?._isInternalUser && currentUser?._internalUserEmail && (
          <Box
            onClick={() => open()}
            sx={{
              mt: 1,
              py: 2,
              mx: 2,
              px: 2,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                width: "100%",
              }}
            >
              <Tooltip
                title={currentUser?._internalUserEmail || ""}
                placement="top-start"
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    color: "text.primary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "pointer",
                    mb: 0.5,
                  }}
                >
                  {t("greeting", { name: capitalize(currentUser?._internalUserEmail.split("@")[0]) })}
                </Typography>
              </Tooltip>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <AdminPanelSettingsOutlinedIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: (theme) => alpha(theme.palette.text.secondary, 0.7),
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: (theme) => alpha(theme.palette.text.secondary, 0.7),
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      cursor: "pointer",
                      lineHeight: 1,
                      mb: 0.25,
                    }}
                  >
                    {t("tenant")}
                  </Typography>
                  <Tooltip
                    title={`${currentUser?.tenant.displayName || ""}`}
                    placement="bottom-start"
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "primary.main",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                    >
                      {currentUser?.tenant.displayName}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
            {config.tenantOnboardUrl && (
              <Tooltip title={t("adminConsole")} placement="top">
                <IconButton
                  size="small"
                  aria-label={t("adminConsole")}
                  // The whole card opens the tenant switcher, so this click must
                  // not bubble or it would do both.
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(
                      config.tenantOnboardUrl,
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                  sx={{
                    flexShrink: 0,
                    color: (theme) => alpha(theme.palette.text.secondary, 0.7),
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
      )}
      <SidebarList
        list={navigationLinks}
        isParentElement
        onCloseSidebar={onCloseSidebar}
        onActive={onActive}
      />
      
    </Box>
  );

  if (isMobileView || fullScreenMode)
    return (
      <Drawer
        open={isSidebarActive}
        onClose={onCloseSidebar}
        PaperProps={{ className: styles.mobileSidebar }}
      >
        <Toolbar className={styles.sidebarHeader}>
          <CasaLogo />
        </Toolbar>
        {sidebarContent}
      </Drawer>
    );

  return sidebarContent;
};

export default Sidebar;
